import { useMemo } from 'react';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Col, Divider, Flex, Row, Select, Space, Tag, Typography } from 'antd';
import {
  AssuntoTreeSelect,
  edicoesDoEnac,
  ULTIMA_EDICAO_PUBLICA,
  type SelecaoDeAssuntos,
} from '@titularis/ui';
import { api } from '../api';

/**
 * Filtros do estudo.
 *
 * A estrutura segue a referência do escopo (telas do QConcursos): os filtros em
 * grade e a busca disparada por botão. Com centenas de questões por busca,
 * refazer a consulta a cada campo mexido custaria caro e faria a lista pular
 * embaixo do dedo - por isso os campos ficam num rascunho e só viram consulta ao
 * clicar em "Buscar".
 *
 * Todos os filtros aceitam vários valores, menos a Disciplina: o aluno escolhe
 * uma disciplina e vê a árvore de assuntos dela (assuntos e delimitações, sim,
 * são vários).
 */
export interface FiltrosDeEstudo {
  years?: string[];
  /** No ENAC, o tempo é a edição ("2025.1"), não o ano. */
  editions?: string[];
  boardIds?: string[];
  notaryOfficeIds?: string[];
  disciplineId?: string;
  subjectIds?: string[];
  subjectDetailIds?: string[];
  /** Tipo da discursiva (2ª fase): dissertação, peça prática ou questão. */
  essayKinds?: string[];
}

/** Filtros de valor múltiplo, cada um com seu rótulo nos chips. */
export type CampoLista = 'years' | 'editions' | 'boardIds' | 'notaryOfficeIds' | 'essayKinds';

const ROTULO_DO_GRUPO: Record<CampoLista, string> = {
  years: 'Ano',
  // No ENAC, o filtro de tempo é a edição ("2025.1 - 1ª Edição").
  editions: 'Ano/Edição',
  boardIds: 'Banca',
  // Internamente segue sendo o cartório (notaryOfficeId); para o aluno, "Estado".
  notaryOfficeIds: 'Estado',
  essayKinds: 'Tipo',
};

/** As partes de uma prova discursiva, para o filtro da 2ª fase. */
const TIPO_DISCURSIVA_OPCOES = [
  { value: 'dissertation', label: 'Dissertação' },
  { value: 'practical_piece', label: 'Peça prática' },
  { value: 'essay_question', label: 'Questão discursiva' },
];

export function PainelDeFiltros({
  rascunho,
  onMudarLista,
  onMudarDisciplina,
  onMudarAssuntos,
  onRemoverValor,
  onRemoverDisciplina,
  onRemoverAssunto,
  onAplicar,
  onLimpar,
  aplicados,
  mostrarBanca,
  mostrarCartorio,
  mostrarClassificacao,
  mostrarTipoDiscursiva,
  porSemestre,
  compacto,
}: {
  rascunho: FiltrosDeEstudo;
  /** Filtros múltiplos mudam em bloco e trazem os rótulos para os chips. */
  onMudarLista: (campo: CampoLista, valores: string[]) => void;
  onMudarDisciplina: (valor?: string) => void;
  onMudarAssuntos: (selecao: SelecaoDeAssuntos) => void;
  onRemoverValor: (campo: CampoLista, valor: string) => void;
  onRemoverDisciplina: () => void;
  onRemoverAssunto: (id: string) => void;
  onAplicar: () => void;
  onLimpar: () => void;
  aplicados: FiltrosDeEstudo;
  mostrarBanca: boolean;
  mostrarCartorio: boolean;
  /** Disciplina/Assunto/Delimitação: escondidos nas discursivas. */
  mostrarClassificacao: boolean;
  /** Tipo (dissertação/peça/questão): só na 2ª fase (discursiva). */
  mostrarTipoDiscursiva: boolean;
  /** No ENAC, filtra por edição (2025.1) em vez de ano. */
  porSemestre: boolean;
  compacto: boolean;
}) {
  const disciplinas = useQuery({
    queryKey: ['disciplines'],
    queryFn: () => api.listDisciplines(),
  });

  const bancas = useQuery({
    queryKey: ['boards'],
    queryFn: async () => (await api.listBoards()).items,
  });

  const cartorios = useQuery({
    queryKey: ['notary-offices'],
    queryFn: async () => (await api.listNotaryOffices()).items,
    enabled: mostrarCartorio,
  });

  const temAssuntoAplicado =
    (aplicados.subjectIds?.length ?? 0) > 0 || (aplicados.subjectDetailIds?.length ?? 0) > 0;

  /*
   * Assunto e delimitação aplicados só têm nome dentro da árvore da disciplina.
   * Sem esta consulta, quem abre a tela por um link pronto vê "Assunto: ..." -
   * o nome só existia na memória de quem tinha acabado de escolher.
   */
  const arvore = useQuery({
    queryKey: ['subject-tree', aplicados.disciplineId],
    queryFn: () => api.subjectTree(aplicados.disciplineId!),
    enabled: Boolean(aplicados.disciplineId) && temAssuntoAplicado,
  });

  /*
   * valor -> rótulo dos chips, montado do que a tela já carrega.
   *
   * Antes este mapa era acumulado conforme a pessoa clicava, e funcionava
   * enquanto ela mesma tivesse escolhido tudo na sessão. Com os filtros na URL
   * isso deixou de valer: link recebido, aba recarregada e botão voltar chegam
   * sem memória nenhuma, e os chips mostrariam identificadores crus.
   */
  const nomes = useMemo(() => {
    const mapa: Record<string, string> = {};
    for (const item of bancas.data ?? []) mapa[item.id] = item.name;
    for (const item of cartorios.data ?? []) mapa[item.id] = item.name;
    for (const item of disciplinas.data ?? []) mapa[item.id] = item.name;
    for (const item of edicoesDoEnac(ULTIMA_EDICAO_PUBLICA)) mapa[item.value] = item.label;
    for (const item of TIPO_DISCURSIVA_OPCOES) mapa[item.value] = item.label;
    for (const assunto of arvore.data ?? []) {
      mapa[assunto.id] = assunto.name;
      for (const delimitacao of assunto.details) mapa[delimitacao.id] = delimitacao.name;
    }
    return mapa;
  }, [bancas.data, cartorios.data, disciplinas.data, arvore.data]);

  const anoAtual = new Date().getFullYear();
  const anos = Array.from({ length: 15 }, (_, indice) => anoAtual - indice);

  const opcoes = (lista?: { id: string; name: string }[]) =>
    (lista ?? []).map((item) => ({ value: item.id, label: item.name }));

  /** Cada filtro em uma coluna: três por linha no desktop, uma no celular. */
  const coluna = compacto ? 24 : 8;

  const grupoVisivel = (campo: CampoLista): boolean => {
    if (campo === 'years') return !porSemestre;
    if (campo === 'editions') return porSemestre;
    if (campo === 'boardIds') return mostrarBanca;
    if (campo === 'essayKinds') return mostrarTipoDiscursiva;
    return mostrarCartorio;
  };

  const chipsDeLista = (
    ['years', 'editions', 'boardIds', 'notaryOfficeIds', 'essayKinds'] as CampoLista[]
  )
    .filter(grupoVisivel)
    .flatMap((campo) =>
      (aplicados[campo] ?? []).map((valor) => ({
        key: `${campo}:${valor}`,
        texto: `${ROTULO_DO_GRUPO[campo]}: ${nomes[valor] ?? valor}`,
        aoFechar: () => onRemoverValor(campo, valor),
      })),
    );

  const chipsDeClassificacao = mostrarClassificacao
    ? [
        ...(aplicados.disciplineId
          ? [
              {
                key: 'disciplina',
                texto: `Disciplina: ${nomes[aplicados.disciplineId] ?? aplicados.disciplineId}`,
                aoFechar: onRemoverDisciplina,
              },
            ]
          : []),
        ...(aplicados.subjectIds ?? []).map((id) => ({
          key: `assunto:${id}`,
          texto: `Assunto: ${nomes[id] ?? '...'}`,
          aoFechar: () => onRemoverAssunto(id),
        })),
        ...(aplicados.subjectDetailIds ?? []).map((id) => ({
          key: `delimitacao:${id}`,
          texto: `Delimitação: ${nomes[id] ?? '...'}`,
          aoFechar: () => onRemoverAssunto(id),
        })),
      ]
    : [];

  const chips = [...chipsDeLista, ...chipsDeClassificacao];

  /** Um Select múltiplo padrão para os filtros de lista. */
  const selectMultiplo = (
    campo: CampoLista,
    placeholder: string,
    itens: { value: string; label: string }[],
    carregando?: boolean,
  ) => (
    <Select
      mode="multiple"
      allowClear
      showSearch
      optionFilterProp="label"
      maxTagCount="responsive"
      style={{ width: '100%' }}
      placeholder={placeholder}
      value={rascunho[campo] ?? []}
      loading={carregando}
      onChange={(valores) => onMudarLista(campo, valores as string[])}
      options={itens}
    />
  );

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Row gutter={[12, 12]}>
        {mostrarTipoDiscursiva ? (
          <Col span={coluna}>{selectMultiplo('essayKinds', 'Tipo', TIPO_DISCURSIVA_OPCOES)}</Col>
        ) : null}
        <Col span={coluna}>
          {porSemestre
            ? selectMultiplo(
                'editions',
                'Ano/Edição',
                edicoesDoEnac(ULTIMA_EDICAO_PUBLICA).map(({ value, label }) => ({ value, label })),
              )
            : selectMultiplo(
                'years',
                'Ano',
                anos.map((ano) => ({ value: String(ano), label: String(ano) })),
              )}
        </Col>
        {mostrarBanca ? (
          <Col span={coluna}>{selectMultiplo('boardIds', 'Banca', opcoes(bancas.data), bancas.isPending)}</Col>
        ) : null}
        {mostrarCartorio ? (
          <Col span={coluna}>
            {selectMultiplo('notaryOfficeIds', 'Estado', opcoes(cartorios.data), cartorios.isFetching)}
          </Col>
        ) : null}
        {mostrarClassificacao ? (
          <>
            <Col span={coluna}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ width: '100%' }}
                placeholder="Disciplina"
                value={rascunho.disciplineId}
                loading={disciplinas.isPending}
                onChange={(valor) => onMudarDisciplina(valor)}
                options={opcoes(disciplinas.data)}
              />
            </Col>
            {/*
              **A mesma largura dos outros, e não o dobro.**

              Com 16, a grade do Enac quebrava torta: os dois primeiros campos
              ficavam em cima com um terço vazio ao lado, e o de assunto descia
              sozinho para a segunda linha, mais largo que os de cima. Três
              campos iguais fecham a primeira linha exata, e na 1ª fase os seis
              fecham duas - que é o que a grade prometia desde o começo.

              O texto também mudou: dizia "Escolha a disciplina" **no campo de
              assunto**, colado embaixo do campo de disciplina, e lia como se os
              dois fossem a mesma coisa.
            */}
            <Col span={coluna}>
              <AssuntoTreeSelect
                disciplineIds={rascunho.disciplineId ? [rascunho.disciplineId] : []}
                carregarArvore={(disciplinaId) => api.subjectTree(disciplinaId)}
                nomeDaDisciplina={(id) => disciplinas.data?.find((item) => item.id === id)?.name}
                value={{
                  subjectIds: rascunho.subjectIds ?? [],
                  subjectDetailIds: rascunho.subjectDetailIds ?? [],
                }}
                onChange={onMudarAssuntos}
                placeholder="Assunto"
              />
            </Col>
          </>
        ) : null}
        {/*
          **O botão ocupa a sobra da grade**, com `flex="auto"`: onde houver
          espaço na linha ele entra nela; onde os campos fecharem a linha
          exata - o caso do Enac, com três -, ele desce e toma a largura
          inteira, ficando alinhado à direita do painel.

          Com uma coluna fixa de 8 ele descia para a segunda linha **à
          esquerda**, embaixo do primeiro campo, parecendo solto no meio do
          nada.
        */}
        <Col flex="auto">
          <Flex justify="flex-end" align="center" gap={8} style={{ height: '100%' }} wrap>
            {chips.length > 0 ? <Button onClick={onLimpar}>Limpar</Button> : null}
            <Button type="primary" icon={<SearchOutlined />} onClick={onAplicar}>
              Buscar questões
            </Button>
          </Flex>
        </Col>
      </Row>

      {chips.length > 0 ? (
        <>
          <Divider style={{ margin: 0 }} />
          <Space size={[6, 6]} wrap>
            <Typography.Text type="secondary">Filtros aplicados:</Typography.Text>
            {chips.map((chip) => (
              <Tag
                key={chip.key}
                closable
                closeIcon={<CloseOutlined />}
                onClose={(evento) => {
                  evento.preventDefault();
                  chip.aoFechar();
                }}
              >
                {chip.texto}
              </Tag>
            ))}
          </Space>
        </>
      ) : null}

    </Space>
  );
}
